import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { useSolicitacaoMutations } from '@/features/SI/hooks/data';
import { comentarioSolicitacaoService } from '@/features/SI/services';
import type { MeuEquipamento } from '@/features/SI/services';
import { descreverEquipamentoResumo } from '@/features/SI/utils/descreverEquipamentoResumo';

type CasoDivergencia = 'nao_aparece' | 'nao_esta_comigo' | 'dados_errados' | 'outro';

/** "Qual é o caso?" — sempre as 4 opções. `pedeItem`: mostra "Qual item?" (obrigatório) quando
 * a lista tem itens; sem itens (ou sem carregar), segue só com o texto. */
const CASOS: ReadonlyArray<{ valor: CasoDivergencia; label: string; pedeItem: boolean }> = [
  { valor: 'nao_aparece', label: 'Estou com um item que não aparece na lista', pedeItem: false },
  { valor: 'nao_esta_comigo', label: 'Tem um item em meu nome que não está comigo', pedeItem: true },
  { valor: 'dados_errados', label: 'Um item da lista está com dados errados', pedeItem: true },
  { valor: 'outro', label: 'Outra coisa', pedeItem: false },
];

interface InformarDivergenciaDrawerProps {
  isOpen: boolean;
  equipamentos: MeuEquipamento[];
  onClose: () => void;
  onCriada: (solicitacaoId: string) => void;
}

/**
 * "Informar divergência" (Meus equipamentos): abre uma Solicitação do tipo
 * 'divergencia_inventario' — sem Cliente, sem Movimentação. Caso + item (se for o caso) +
 * texto viram a 1ª postagem das Observações, como no formulário normal. O SI corrige o
 * Inventário, responde por ali e conclui.
 */
export function InformarDivergenciaDrawer({ isOpen, equipamentos, onClose, onCriada }: InformarDivergenciaDrawerProps) {
  const { user } = useAuth();
  const toast = useToast();
  const { criarSolicitacao } = useSolicitacaoMutations();
  const [caso, setCaso] = useState<CasoDivergencia | ''>('');
  const [equipamentoId, setEquipamentoId] = useState('');
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const casoEscolhido = CASOS.find((c) => c.valor === caso);
  const casoPedeItem = (casoEscolhido?.pedeItem ?? false) && equipamentos.length > 0;
  const podeEnviar = caso !== '' && texto.trim() !== '' && (!casoPedeItem || equipamentoId !== '');

  const limpar = () => {
    setCaso('');
    setEquipamentoId('');
    setTexto('');
  };

  const fechar = () => {
    if (enviando) return;
    limpar();
    onClose();
  };

  const enviar = async () => {
    if (!user?.collaboratorId || !user.email || !podeEnviar) return;
    setEnviando(true);
    try {
      const solicitacaoId = await criarSolicitacao.mutateAsync({
        tipoSolicitacao: 'divergencia_inventario',
        colaboradorId: user.collaboratorId,
        colaboradorEmail: user.email,
        colaboradorNome: user.displayName ?? user.email,
        solicitanteId: user.collaboratorId,
        solicitanteEmail: user.email,
        clienteId: null,
      });
      const item = casoPedeItem ? equipamentos.find((e) => e.id === equipamentoId) : undefined;
      const descricaoItem = item ? descreverEquipamentoResumo(item) : null;
      const cabecalho = [
        `Caso: ${casoEscolhido?.label}`,
        descricaoItem ? `Item: ${descricaoItem.tipo} · ${descricaoItem.detalhe}` : null,
      ]
        .filter(Boolean)
        .join('\n');
      try {
        await comentarioSolicitacaoService.create(
          solicitacaoId,
          `${cabecalho}\n\n${texto.trim()}`,
          { id: user.collaboratorId, name: user.displayName ?? user.email, avatar: user.photoURL ?? null },
          user.email
        );
      } catch (error) {
        console.error('Erro ao enviar a descrição da divergência:', error);
        toast.warning('Aviso criado, mas a descrição não foi salva — escreva de novo nas Observações.');
      }
      toast.success('Aviso enviado ao SI.');
      limpar();
      onCriada(solicitacaoId);
    } catch (error) {
      // Erro já reportado via toast pelo hook (onError).
      console.error('Erro ao informar divergência:', error);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={fechar} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex justify-end">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="flex h-full w-full max-w-lg flex-col overflow-hidden bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-raro-gray-200 p-4">
                <Dialog.Title className="font-montserrat text-lg font-semibold text-raro-gray-900">
                  Informar divergência
                </Dialog.Title>
                <button type="button" className="text-raro-gray-400 hover:text-raro-gray-500" onClick={fechar}>
                  <span className="sr-only">Fechar</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="flex-1 space-y-5 overflow-y-auto p-5">
                <p className="text-sm text-raro-gray-600">
                  Avise o SI quando a lista de Meus equipamentos não bater com o que está com você. O SI confere,
                  corrige o Inventário e responde nas Observações do chamado.
                </p>

                <fieldset>
                  <legend className="block text-sm font-medium text-raro-gray-700">Qual é o caso? *</legend>
                  <div className="mt-2 space-y-2">
                    {CASOS.map((opcao) => (
                      <label
                        key={opcao.valor}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors ${
                          caso === opcao.valor
                            ? 'border-primary-300 bg-primary-50 text-primary-800'
                            : 'border-raro-gray-200 text-raro-gray-700 hover:border-primary-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="divergencia-caso"
                          className="h-4 w-4 border-raro-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={caso === opcao.valor}
                          onChange={() => setCaso(opcao.valor)}
                        />
                        {opcao.label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                {casoPedeItem && (
                  <div>
                    <label htmlFor="divergencia-item" className="block text-sm font-medium text-raro-gray-700">
                      Qual item? *
                    </label>
                    <select
                      id="divergencia-item"
                      className="input-primary mt-1 w-full"
                      value={equipamentoId}
                      onChange={(event) => setEquipamentoId(event.target.value)}
                    >
                      <option value="">Selecione</option>
                      {equipamentos.map((equipamento) => {
                        const { tipo, detalhe } = descreverEquipamentoResumo(equipamento);
                        return (
                          <option key={equipamento.id} value={equipamento.id}>
                            {tipo} · {detalhe}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                <div>
                  <label htmlFor="divergencia-texto" className="block text-sm font-medium text-raro-gray-700">
                    Conte com mais detalhes *
                  </label>
                  <textarea
                    id="divergencia-texto"
                    rows={5}
                    maxLength={4000}
                    placeholder="Ex.: estou com um monitor Dell desde março; ou: esse headset eu devolvi em setembro."
                    className="input-primary mt-1 w-full"
                    value={texto}
                    onChange={(event) => setTexto(event.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-raro-gray-200 p-4">
                <button type="button" className="btn-secondary" onClick={fechar} disabled={enviando}>
                  Cancelar
                </button>
                <button type="button" className="btn-primary" onClick={enviar} disabled={enviando || !podeEnviar}>
                  {enviando ? 'Enviando...' : 'Enviar ao SI'}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
